import { AddressWithComponents, Suggestion } from '../types/smarty-streets';
import {
  buildAutocompleteClient,
  createFilteredLookup,
  suggestionsToAddresses,
} from '../utils/smarty-streets';
import { debounce } from 'lodash-es';
import { useState, useEffect } from 'react';

interface AutoCompleteProps {
  inputValue: string;
  smartyStreetsEmbeddedKey: string;
  INPUT_CHARS_LENGTH?: number;
  INPUT_TEXT_DEBOUNCE?: number;
  skip?: boolean;
  onSuggestions?: (suggestions: Suggestion[]) => void;
}

function useAutoComplete({
  inputValue,
  smartyStreetsEmbeddedKey,
  INPUT_CHARS_LENGTH = 3,
  INPUT_TEXT_DEBOUNCE = 200,
  skip,
  onSuggestions,
}: AutoCompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressWithComponents[] | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const debouncedFetchSuggestions = debounce(async () => {
      if (skip || inputValue.length < INPUT_CHARS_LENGTH) {
        setSuggestions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const client = buildAutocompleteClient(smartyStreetsEmbeddedKey);
      const lookup = createFilteredLookup(inputValue);

      try {
        const response = await client.send(lookup);
        setSuggestions(suggestionsToAddresses(response.result));
        setLoading(false);
        if (onSuggestions) onSuggestions(response.result);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          setError(error.message);
        } else {
          setError('An unexpected error occurred.');
        }

        setLoading(false);
        setSuggestions([]);
      }
    }, INPUT_TEXT_DEBOUNCE);

    debouncedFetchSuggestions();

    return () => {
      if (debouncedFetchSuggestions.cancel) {
        debouncedFetchSuggestions.cancel();
      }
    };
  }, [
    INPUT_CHARS_LENGTH,
    INPUT_TEXT_DEBOUNCE,
    smartyStreetsEmbeddedKey,
    inputValue,
    onSuggestions,
    skip,
  ]);

  return { suggestions, loading, error };
}

export { useAutoComplete, type AutoCompleteProps };
